




























































































































































import {
  EbtoEmployerResult,
  CreateTransferOutCampaignRequest,
  CampaignRetentionDataModel
} from "@/admin/types";
import {
  Vue,
  Component,
  Watch,
  Prop
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import moment from "moment";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";
import { useTransferOutStore } from "@/admin/store/transferOut";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import "moment-timezone";
import { UserPermission } from "@/shared/types";

const zone = "America/Chicago";
@Component({})
export default class CampaignDetail extends Vue {
  $validator: Validator;
  @Prop({ type: String, required: false })
  title: string;
  @Prop({ required: false })
  campaignKey: string;
  @Prop({ required: false })
  employer: EbtoEmployerResult;

  moment = moment;
  today = moment().toDate();
  tomorrow = moment().add(1, "d").toDate();

  formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  });

  hasPricingOption = {
    getMessage () {
      return "Please choose a Pricing Offer option.";
    },
    validate (value: boolean) {
      return value;
    }
  };

  validateDateIsAfterPrevious = {
    getMessage () {
      return "Date must be after the previous date.";
    },
    validate (toDate, args) {
      if (args[0] === "null") {
        return true;
      }
      return moment(toDate).isAfter(args[0]);
    }
  };

  get canEbtoUserEdit (): boolean {
    return this.featureFlagStore.flags.hsa4475allowRolePermissions ?
    this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalEbtoEdit)
    || this.userStore.currentUser.groups.some(x => x === "ebto-edit")
    : this.userStore.currentUser.groups.some(x => x === "ebto-edit");
  }

  get incentiveAmountValidator () {
    return {
      required: this.hasBonusIncentive,
      decimal: 2,
      min_value_usd: 0.01,
      max_value_usd: 100
    };
  }

  userStore = useUserStore();
  notifierStore = useNotifierStore();
  transferOutStore = useTransferOutStore();
  featureFlagStore = useFeatureFlagStore();
  rolePermissionsStore = useRolePermissionsStore();

  isNewCampaign = false;
  isReadOnly = true;
  isCampaignExpired = true;
  isFutureCampaign = true;
  campaign: CreateTransferOutCampaignRequest = {
    name: "",
    employerId: 0,
    dateStart: undefined,
    dateEnd: undefined,
    dateDistributionsEnd: undefined,
    dateCutOff: undefined,
    dateTransfer: undefined,
    newCustodian: "",
    newCustodianEffectiveDate: undefined,
    bonus: 0,
    isAdminFeeWaived: false,
    employerName: "",
    employerExtId: ""
  };
  retentionData: CampaignRetentionDataModel = {
    totalAccountsRetained: 0,
    totalDepositsRetained: 0
  };
  hasBonusIncentive = false;
  hasSpecialOffer = false;
  hasAdminFees = false;
  campaignName = "";

  mounted (): void {
    this.fetchCampaignDetails();
    this.$validator.extend("hasPricingOption", this.hasPricingOption);
    this.$validator.extend("validateDateIsAfterPrevious", this.validateDateIsAfterPrevious);
  }

  @Watch("employer", { deep: true })
  updateCampaignWhenEmployerUpdated () {
    if (!this.campaignKey) {
      this.campaign.name = this.employer.name + " " + new Date().getFullYear();
      this.campaign.employerId = this.employer.id;
      this.campaign.employerExtId = this.employer.externalEmployerId;
      this.campaign.employerName = this.employer.name;
    }
  }

  fetchCampaignDetails (): void {
    if (this.campaignKey) {
      this.transferOutStore.getCampaignDetails(this.campaignKey).then(data => {
        this.campaign = this.transferOutStore.CurrentCampaign;
        this.employer.id = Number(this.campaign.employerId);
        this.employer.name = this.campaign.employerName;
        this.employer.externalEmployerId = this.campaign.employerExtId;

        // Set the flag that the campaign already exists
        this.isNewCampaign = false;
        this.isReadOnly = true;
        this.hasBonusIncentive = this.campaign.bonus > 0;
        this.hasSpecialOffer = (this.hasBonusIncentive || this.campaign.isAdminFeeWaived);
        const expiDate = moment(this.campaign.dateEnd).tz(zone).add(1, "d");
        const currDate = moment().tz(zone).format("L");
        this.isCampaignExpired = moment(expiDate.format("L")).isBefore(moment(currDate));
        this.isFutureCampaign = moment(this.campaign.dateStart).isAfter();
      });
      this.transferOutStore.getCampaignRetentionDetails(this.campaignKey).then(data => {
        if (this.transferOutStore.campaignRetentionData) {
          this.retentionData = this.transferOutStore.campaignRetentionData;
        }
      });

    } else {
      // Set the flag that the campaign needs to be created
      this.isNewCampaign = true;
      this.isReadOnly = false;
    }
  }

  handleError (err: any): void {
    this.showError(
      (err && err.errorMessage) ||
      "Error processing your request. Please try again later."
    );
  }

  showError (message: string): void {
    this.notifierStore.setDanger(message);
  }

  onSubmit () {
    this.$validator.validate().then(valid => {
      if (valid) {
        this.updatePricingFields();
        if (this.isNewCampaign) {
          this.transferOutStore.createCampaign(this.campaign).then(resp => {
            if (resp) {
              this.notifierStore.setSuccess("Campaign created successfully.");
              this.$router.push("/utility/transfer-out");
            } else {
              this.showError("An error occured when creating the campaign. Please try again.");
            }
          });
        } else {
          this.transferOutStore.updateCampaign(this.campaign).then(resp => {
            if (resp) {
              this.notifierStore.setSuccess("Campaign updated successfully.");
              this.$router.push(`/ebto/transfer-out/view-campaign/${this.campaignKey}`);
              this.isReadOnly = true;
              this.fetchCampaignDetails();
            } else {
              this.showError("An error occured when updating the campaign. Please try again.");
            }
          });
        }
      }
    });
  }

  updatePricingFields () {
    if (!this.hasSpecialOffer) {
      this.hasBonusIncentive = false;
      this.campaign.bonus = 0;
      this.campaign.isAdminFeeWaived = false;
    } else if (!this.hasBonusIncentive) {
      this.campaign.bonus = 0;
    }
  }

  onCancel () {
    if (this.isReadOnly || this.isNewCampaign) {
      this.$router.push("/utility/transfer-out");
    } else {
      this.isReadOnly = true;
    }
  }
}
